import { motion } from 'framer-motion';
import { Users, Award, Clock, Sparkles } from 'lucide-react';

const AboutSection = ({ about }) => {
  const defaultStats = [
    { icon: Users, value: '50+', label: 'Happy Clients' },
    { icon: Award, value: '100+', label: 'Projects Done' },
    { icon: Clock, value: '5+', label: 'Years Experience' },
    { icon: Sparkles, value: '24/7', label: 'Support' },
  ];

  const stats = about?.stats?.length > 0 ? about.stats : defaultStats;

  return (
    <section id="about" className="section-spacing" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src={about?.image_url || "https://images.unsplash.com/photo-1622676614630-a9109126264a?w=800"}
                alt="Team"
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 rounded-2xl bg-primary/10 -z-10" />
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-xl bg-secondary/10 -z-10" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              About Us
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-testid="about-title">
              {about?.title || 'Crafting Digital Excellence'}
            </h2>
            <p className="text-lg text-muted-foreground mb-6" data-testid="about-description">
              {about?.description || 'We are a team of passionate developers, designers, and strategists dedicated to creating exceptional digital experiences.'}
            </p>
            <p className="text-muted-foreground mb-8">
              {about?.mission || 'Our mission is to help businesses thrive in the digital age by delivering innovative, scalable, and user-centric solutions.'}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map((stat, index) => {
                const Icon = stat.icon || Sparkles;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      {typeof Icon === 'function' ? <Icon className="w-6 h-6 text-primary" /> : null}
                    </div>
                    <div className="text-2xl font-heading font-bold text-primary">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
